import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import KushkiCard from './kushki-forms/kushki-card';
import KushkiCash from './kushki-forms/kushki-cash';
import KushkiTransfer from './kushki-forms/kushki-transfer';
import { KushkiCardType, KushkiCashType, KushkiTransferType } from './types/kushki-types';
import { KUSHKI_ALLOWED_METHODS, KUSHKI_METHODS, KushkiDataType } from './constants/kushki';
import {
    getCurrentTypeOfData,
    getKushkiPaymentRequest,
    isNeedValidateKushkiForm,
    validateKushkiForm
} from './utils/kushki';
import { LATINA_COUNTRY_CODE } from '../../../../constants/latina-country';
import { addPaymentError, changeState, resetPaymentErrors, validateForm } from '../../reducers';
import { PAYMENT_METHOD_KEYS } from '../../../../constants/payment-method-keys';
import qualityEvents from '../../../../services/quality-events/quality-events';
import formatPrice from '../../../../utils/price/format-price';
import usePaymentOptions from '../../../../services/payment/use-payment-options';
import './kushki.scss';

type KushkiPaymentProps = {};

const KushkiPaymentForm: FC<KushkiPaymentProps> = () => {
    const dispatch = useDispatch();
    const user = useSelector((state: RootState) => state.main.user);
    const currency = useSelector((state: RootState) => state.main.currency);
    const {
        activePaymentOption,
        formSubmitAttempt,
        paymentCart,
        formData: invoiceFormData,
        withInvoice
    } = useSelector((state: RootState) => state.payment);
    const { data: paymentOptionsData } = usePaymentOptions(currency.code);
    const [kushkiFormData, setKushkiFormData] = useState<KushkiDataType | {}>({});
    const [formErrors, setFormErrors] = useState<string[]>([]);

    useEffect(() => {
        if (!paymentOptionsData?.loaded || !activePaymentOption) {
            return;
        }

        setKushkiFormData(getCurrentTypeOfData(activePaymentOption.id as KUSHKI_METHODS, paymentOptionsData.userCountry as LATINA_COUNTRY_CODE));
    }, [paymentOptionsData, activePaymentOption]);

    useEffect(() => {
        if (
            !formSubmitAttempt
            || !activePaymentOption
            || !paymentOptionsData?.loaded
            || !paymentCart?.loaded
            || Object.keys(kushkiFormData).length
            || KUSHKI_ALLOWED_METHODS.includes(activePaymentOption.id as PAYMENT_METHOD_KEYS)
        ) {
            return;
        }

        dispatch(resetPaymentErrors());

        if (isNeedValidateKushkiForm(activePaymentOption.id as PAYMENT_METHOD_KEYS, '')) {
            const kushkiFormErrors = validateKushkiForm(
                activePaymentOption.id as KUSHKI_METHODS,
                kushkiFormData as KushkiTransferType & KushkiCashType & KushkiCardType,
                paymentOptionsData?.userCountry || ''
            );

            if (kushkiFormErrors.length) {
                setFormErrors(kushkiFormErrors);

                return;
            }
        }

        const isInvoiceFormValid = dispatch(validateForm());

        if (!isInvoiceFormValid) {
            return;
        }

        setFormErrors([]);
        dispatch(changeState({ isDataProcessing: true }));
        qualityEvents.checkoutPage.startPayment(user.id, activePaymentOption.id, paymentCart?.finalPrice);

        const kushkiPaymentRequest = getKushkiPaymentRequest({
            paymentOptionsData,
            paymentMethod: activePaymentOption.id as PAYMENT_METHOD_KEYS,
            kushkiFormData: kushkiFormData as KushkiDataType,
            invoiceFormData,
            withInvoice: Boolean(withInvoice),
            price: formatPrice(paymentCart?.finalPrice),
            user,
            currency
        });

        kushkiPaymentRequest
            .then(response => {
                if (!response?.endpoint) {
                    dispatch(addPaymentError({}));
                    qualityEvents.checkoutPage.failedPayment(
                        user.id,
                        activePaymentOption.id,
                        paymentCart?.finalPrice,
                        response?.error?.message
                    );

                    return;
                }

                window.location.href = response.endpoint;
            })
            .catch((err) => {
                dispatch(addPaymentError({}));
                qualityEvents.checkoutPage.failedPayment(
                    user.id,
                    activePaymentOption.id,
                    paymentCart?.finalPrice,
                    err?.data?.message || err?.data?.reason
                );
            })
            .finally(() => dispatch(changeState({ isDataProcessing: false })))

    }, [
        formSubmitAttempt,
        activePaymentOption,
        paymentOptionsData,
        paymentCart,
        kushkiFormData
    ]);

    const handleDataChange = (name: string, value: string) => {
        setKushkiFormData((prev) => ({ ...prev, [name]: value }));
    };

    if (!activePaymentOption || !Object.keys(kushkiFormData).length || !paymentOptionsData?.userCountry) {
        return <></>;
    }

    if (activePaymentOption.id === KUSHKI_METHODS.KUSHKI_CARD) {
        return (
            <KushkiCard
                CVCLength={paymentOptionsData.userCountry === LATINA_COUNTRY_CODE.PE ? 4 : 3}
                cardData={kushkiFormData as KushkiCardType}
                formErrors={formErrors}
                onCardDataChange={handleDataChange}
            />
        );
    }
    if (activePaymentOption.id === KUSHKI_METHODS.KUSHKI_CASH) {
        return (
            <KushkiCash
                cashData={kushkiFormData as KushkiCashType}
                formErrors={formErrors}
                region={paymentOptionsData.userCountry as LATINA_COUNTRY_CODE}
                onCashDataChange={handleDataChange}
            />
        );
    }
    if (activePaymentOption.id === KUSHKI_METHODS.KUSHKI_TRANSFER) {
        return (
            <KushkiTransfer
                formErrors={formErrors}
                region={paymentOptionsData.userCountry as LATINA_COUNTRY_CODE}
                transferData={kushkiFormData as KushkiTransferType}
                onTransferDataChange={handleDataChange}
            />
        );
    }

    return <div />;
};

export default KushkiPaymentForm;
